<template>
    <div class="modal fade createNewModal" id="AddModalAffectation" tabindex="-1" ref="addAffectationModalRef"
        aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content p-15 p-md-40">
                <div class="modal-header d-block ps-0 pe-0 pt-0 pb-15 pb-md-25">
                    <h5 class="modal-title fw-bold text-black">{{ title }}</h5>
                </div>

                <div class="modal-body ps-0 pe-0 pb-0 pt-15 pt-md-25">
                    <Form ref="affectationForm" @submit="addAffectation" :validation-schema="affectationSchema">
                        <div class="row">
                            <!-- Message de confirmation avec le bon nom de file -->
                            <div class="col-md-12">
                                <div class="form-group mb-25">
                                    <p class="form-control-plaintext fs-16 fw-medium">
                                        Voulez-vous vraiment affecter la file <strong>{{ selectedFileLabel }}</strong> à
                                        des agents ?
                                    </p>
                                </div>
                            </div>

                            <!-- Sélection multiple des utilisateurs -->
                            <div class="col-md-12">
                                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                                    <label class="d-block text-black fw-semibold mb-10">
                                        Utilisateurs <span class="text-danger">*</span>
                                    </label>
                                    <Field name="user" v-slot="{ field, errorMessage }">
                                        <Multiselect v-model="selectedUsers" v-bind="field" :options="userOptions"
                                            :searchable="true" :multiple="true" mode="tags" track-by="value"
                                            label="label" placeholder="Sélectionner un ou plusieurs utilisateurs"
                                            :class="{ 'is-invalid': errorMessage }" />
                                        <div class="text-danger mt-1" v-if="errorMessage">{{ errorMessage }}</div>
                                    </Field>
                                    <ErrorMessage name="user" class="text-danger" />
                                </div>
                            </div>

                            <!-- Bouton submit -->
                            <div class="col-12 text-end">
                                <button type="submit"
                                    class="default-btn transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16">
                                    {{ btntext }}
                                </button>
                            </div>
                        </div>
                    </Form>
                </div>

                <!-- Bouton fermer -->
                <button type="button" class="btn-close shadow-none" data-bs-dismiss="modal" aria-label="Close"
                    @click="resetValue()"></button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch, nextTick } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import ApiService from '../../services/ApiService';
import * as Yup from 'yup';
import { error, success, hideModal } from '../../utils/utils';
import Multiselect from '@vueform/multiselect';
import JwtService from '@/services/JwtService';
import router from '@/router';

export default defineComponent({
    name: "AddModalAffectation",
    components: {
        Form,
        Field,
        ErrorMessage,
        Multiselect,
    },
    props: {
        validationQueue: { type: Object, required: true },
    },
    setup(props, { emit }) {
        // Schema : au moins un utilisateur requis
        const affectationSchema = Yup.object().shape({
            user: Yup.array()
                .required('Veuillez sélectionner au moins un utilisateur'),
        });

        const affectationForm = ref<InstanceType<typeof Form> | null>(null);
        const addAffectationModalRef = ref<null | HTMLElement>(null);
        const title = ref('Affecter une file à des agents');
        const btntext = ref('Affecter');
        const selectedFileLabel = ref('');
        const userOptions = ref([]);
        const selectedUsers = ref<string[]>([]); // tableau d'IDs

        // Mise à jour du nom de la file quand la prop change
        watch(
            () => props.validationQueue,
            (newVal) => {
                if (newVal) {
                    selectedFileLabel.value = newVal.name || newVal.code || 'Inconnu';
                } else {
                    selectedFileLabel.value = '';
                }
            },
            { immediate: true }
        );
        // Chargement des utilisateurs
        const getUsers = async () => {
            const user = JwtService.getUser();
            console.log('USER JWT =', user);
            if (!user) return error("Utilisateur non connecté");

            try {
                const data = await ApiService.get('/user?limit=0');
                userOptions.value = data.data.data.data
                    .filter((u: any) => u.id !== user.userId)
                    .map((u: any) => ({
                        label: `${u.nom} ${u.prenom}`,
                        value: u.id,
                    }));
            } catch (err: any) {
                error(err?.response?.data?.message || 'Erreur lors de la récupération des utilisateurs');
            }
        };
        // Soumission
        const addAffectation = async (values: any, { resetForm }: any) => {
            try {
                const currentUser = JwtService.getUser();
                if (!currentUser) return error("Utilisateur non connecté");
                const selectedUserIds = selectedUsers.value;
                if (!selectedUserIds || selectedUserIds.length === 0) {
                    return error("Veuillez sélectionner au moins un utilisateur.");
                }
                console.log("UserIDs envoyés :", selectedUserIds);
                const response = await ApiService.post('/agvalidationq', {
                    userIds: selectedUserIds,
                    validationQueueId: props.validationQueue.id,
                    userCreation: currentUser.userId,
                });
                // success('Assignation effectuée avec succès !');
                success(response.data.message || 'Assignation effectuée avec succès !');
                hideModal(addAffectationModalRef.value);
                resetForm();
                router.push({ name: "ListeAffectationPage" })
                selectedUsers.value = []; // reset la sélection multiple
                emit('refresh');
            } catch (err: any) {
                error(err?.response?.data?.message || 'Erreur serveur');
            }
        };
        // Reset à la fermeture
        const resetValue = () => {
            affectationForm.value?.resetForm();
            selectedFileLabel.value = '';
        };

        onMounted(() => {
            getUsers();
        });

        return {
            affectationForm,
            addAffectationModalRef,
            title,
            btntext,
            selectedFileLabel,
            userOptions,
            affectationSchema,
            addAffectation,
            resetValue,
            selectedUsers
        };
    },
});
</script>